import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { userAccountApi } from '../../../../shared/services/userAccountApi'

interface Plan {
  _id: string
  name: string
  type: string
  displayName: string
  description: string
  price: {
    monthly: number
    yearly: number
    yearlyDiscount?: number
  }
  features: {
    linksPerMonth: number
    clicksPerMonth: number
    customDomains: number
    teamMembers: number
    analytics: boolean
    apiAccess: boolean
    qrCodes: boolean
    passwordProtection: boolean
    expirationDates: boolean
    customSlug: boolean
    whiteLabel: boolean
    bulkOperations: boolean
    advancedSecurity: boolean
    prioritySupport: boolean
    sslCertificates: boolean
    webhooks: boolean
  }
  popular?: boolean
  badge?: string
  color?: string
  isCurrent: boolean
  isUpgrade: boolean
  isDowngrade: boolean
}

export interface PlansData {
  currentPlan: string
  plans: Plan[]
}

interface PlanComparisonProps {
  data: PlansData
  onClose?: () => void
}

const PlanComparison: React.FC<PlanComparisonProps> = ({ data, onClose }) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
    'monthly'
  )
  const [changingPlan, setChangingPlan] = useState<string | null>(null)
  const [showDowngradeWarning, setShowDowngradeWarning] = useState<{
    show: boolean
    plan: Plan | null
    warnings: string[]
  }>({ show: false, plan: null, warnings: [] })

  const queryClient = useQueryClient()

  // Plan change mutation
  const changePlanMutation = useMutation({
    mutationFn: async ({
      planId,
      planType,
    }: {
      planId?: string
      planType?: string
    }) => {
      return await userAccountApi.changePlan({ planId, planType })
    },
    onSuccess: response => {
      setChangingPlan(null)

      // Check if there are warnings (downgrade with usage exceeding limits)
      const warnings = response.data?.warnings ?? []
      if (warnings.length > 0) {
        setShowDowngradeWarning({
          show: true,
          plan: null,
          warnings,
        })

        // Show warnings but close the main modal after a delay
        setTimeout(() => {
          onClose?.()
          // Refresh subscription data
          queryClient.invalidateQueries({ queryKey: ['user-subscription'] })
          queryClient.invalidateQueries({ queryKey: ['available-plans'] })
        }, 3000)
      } else {
        // Successful change without warnings
        onClose?.()
        // Refresh subscription data
        queryClient.invalidateQueries({ queryKey: ['user-subscription'] })
        queryClient.invalidateQueries({ queryKey: ['available-plans'] })
      }
    },
    onError: (error: Error) => {
      setChangingPlan(null)
      console.error('Plan change failed:', error)
      // You might want to show a toast notification here
    },
  })

  const handlePlanSelect = async (plan: Plan) => {
    if (plan.isCurrent || changingPlan) {
      return
    }

    setChangingPlan(plan._id)

    try {
      await changePlanMutation.mutateAsync({
        planId: plan._id,
        planType: plan.type,
      })
    } catch (error) {
      console.error('Plan selection failed:', error)
    }
  }

  // Feature display names and descriptions
  const featureLabels = {
    linksPerMonth: 'Short Links per Month',
    clicksPerMonth: 'Clicks per Month',
    customDomains: 'Custom Domains',
    teamMembers: 'Team Members',
    analytics: 'Advanced Analytics',
    apiAccess: 'API Access',
    qrCodes: 'QR Code Generator',
    passwordProtection: 'Password Protection',
    expirationDates: 'Link Expiration',
    customSlug: 'Custom Short Codes',
    whiteLabel: 'White Label',
    bulkOperations: 'Bulk Operations',
    advancedSecurity: 'Advanced Security',
    prioritySupport: 'Priority Support',
    sslCertificates: 'SSL Certificates',
    webhooks: 'Webhooks & Integrations',
  }

  // Format feature value for display
  const formatFeatureValue = (value: any, featureKey: string) => {
    if (typeof value === 'boolean') {
      return value
    }

    if (typeof value === 'number') {
      if (value === -1) {
        return 'Unlimited'
      }

      if (featureKey === 'clicksPerMonth' || featureKey === 'linksPerMonth') {
        return value.toLocaleString()
      }

      return value
    }

    return value
  }

  // Get price for current billing cycle
  const getPrice = (plan: Plan) => {
    return billingCycle === 'monthly'
      ? plan.price.monthly
      : plan.price.yearly / 12
  }

  // Get savings for yearly billing
  const getYearlySavings = (plan: Plan) => {
    if (plan.price.monthly === 0) {
      return 0
    }
    const monthlyTotal = plan.price.monthly * 12
    return Math.round(((monthlyTotal - plan.price.yearly) / monthlyTotal) * 100)
  }

  // PlanCard 서브컴포넌트
  const PlanCard: React.FC<{ plan: Plan; index: number }> = ({
    plan,
    index,
  }) => {
    const price = getPrice(plan)
    const yearlySavings = getYearlySavings(plan)

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`relative rounded-lg border-2 bg-white p-6 dark:bg-gray-700 ${
          plan.isCurrent
            ? 'border-blue-500 dark:border-blue-400'
            : plan.popular
              ? 'border-purple-500 dark:border-purple-400'
              : 'border-gray-200 dark:border-gray-600'
        } ${plan.popular ? 'shadow-lg' : 'shadow-sm'}`}
      >
        {/* Popular Badge */}
        {plan.popular && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
            <div className="flex items-center gap-1 rounded-full bg-purple-500 px-3 py-1 text-xs font-bold text-white">
              <SparklesIcon className="h-3 w-3" />
              {plan.badge || 'Most Popular'}
            </div>
          </div>
        )}

        {/* Current Plan Badge */}
        {plan.isCurrent && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
            <div className="flex items-center gap-1 rounded-full bg-blue-500 px-3 py-1 text-xs font-bold text-white">
              <CheckIcon className="h-3 w-3" />
              Current Plan
            </div>
          </div>
        )}

        {/* Plan Header */}
        <div className="mb-6 text-center">
          <h3 className="mb-2 text-lg font-bold text-gray-900 dark:text-white">
            {plan.displayName}
          </h3>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {plan.description}
          </p>

          {/* Price */}
          <div className="mb-4">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              ₩{price === 0 ? 'Free' : price.toFixed(0)}
              {price > 0 && (
                <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                  /{billingCycle === 'monthly' ? 'month' : 'month'}
                </span>
              )}
            </div>

            {billingCycle === 'yearly' && yearlySavings > 0 && (
              <div className="text-sm font-medium text-green-600 dark:text-green-400">
                Save {yearlySavings}% annually
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={() => handlePlanSelect(plan)}
          disabled={plan.isCurrent || changingPlan === plan._id}
          className={`mb-6 w-full rounded-lg px-4 py-2 font-medium transition-colors ${
            plan.isCurrent
              ? 'cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
              : changingPlan === plan._id
                ? 'cursor-not-allowed bg-gray-400 text-white'
                : plan.isUpgrade
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : plan.isDowngrade
                    ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
          }`}
        >
          {changingPlan === plan._id ? (
            <div className="flex items-center justify-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Changing...
            </div>
          ) : plan.isCurrent ? (
            'Current Plan'
          ) : plan.isUpgrade ? (
            'Upgrade'
          ) : plan.isDowngrade ? (
            'Downgrade'
          ) : (
            'Select Plan'
          )}
        </button>

        {/* Feature List */}
        <div className="space-y-3">
          {Object.entries(featureLabels).map(([key, label]) => {
            const value = plan.features[key as keyof typeof plan.features]
            const formattedValue = formatFeatureValue(value, key)

            return (
              <div key={key} className="flex items-center gap-3 text-sm">
                {typeof value === 'boolean' ? (
                  value ? (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 text-green-500" />
                  ) : (
                    <XMarkIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  )
                ) : (
                  <CheckIcon className="h-4 w-4 flex-shrink-0 text-green-500" />
                )}

                <span className="text-gray-700 dark:text-gray-300">
                  {typeof value === 'boolean'
                    ? label
                    : `${formattedValue} ${label}`}
                </span>
              </div>
            )
          })}
        </div>
      </motion.div>
    )
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 p-6 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Choose Your Plan
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Compare features and select the plan that works best for you
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center gap-4">
            <div className="flex items-center rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                  billingCycle === 'yearly'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Yearly
                <span className="ml-1 rounded bg-green-100 px-1 text-xs text-green-600 dark:bg-green-900 dark:text-green-300">
                  Save 20%
                </span>
              </button>
            </div>

            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {data.plans.map((plan, index) => (
            <PlanCard key={plan._id} plan={plan} index={index} />
          ))}
        </div>
      </div>

      {/* Downgrade Warning Modal */}
      {showDowngradeWarning.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-800"
          >
            <div className="mb-4 flex items-center gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-orange-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Plan Downgraded Successfully
              </h3>
            </div>

            <div className="mb-6 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your plan has been successfully downgraded, but please note:
              </p>

              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
                <ul className="space-y-1 text-sm text-orange-700 dark:text-orange-300">
                  {showDowngradeWarning.warnings.map((warning, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="mt-0.5 text-orange-500">•</span>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Your existing links and data remain intact, but you may not be
                able to create new links until your usage falls within the new
                plan limits.
              </p>
            </div>

            <button
              onClick={() =>
                setShowDowngradeWarning({
                  show: false,
                  plan: null,
                  warnings: [],
                })
              }
              className="w-full rounded-lg bg-orange-600 px-4 py-2 font-medium text-white transition-colors hover:bg-orange-700"
            >
              I Understand
            </button>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default PlanComparison
